import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { restCacheConfig } from '../config/axios';
import { CONFIG } from '../config/config';
import { QUERY_KEYS } from '../hooks/hooksUtils';
import { useInfiniteQueryData } from '../hooks/useInfiniteQueryData';
import { useQueryData } from '../hooks/useQueryData';
import { useUserStore } from '../stores/UserStore';
import { ACTION_TYPES } from '../types/postsTypings';
import { USER_ROLES, USER_STATUS, USER_TYPE } from '../types/userTypings';
import { RestService } from './RestService';
import { MULTIPART_HEADERS } from './Utils';

export const fetchSuggestions = () => {
  return RestService.get(`/users/suggestions`);
};

export const useSuggestionsData = () => {
  return useQueryData([QUERY_KEYS.SUGGESTIONS], fetchSuggestions, restCacheConfig);
};

export const fetchAllUsers = () => {
  return RestService.get(`/users/all`);
};

export const useUsersAllData = () => {
  return useQueryData([QUERY_KEYS.USERS_ALL], fetchAllUsers, restCacheConfig);
};

export const fetchTeam = () => {
  return RestService.get(`/users/team`);
};

export const useTeamData = () => {
  return useQueryData([QUERY_KEYS.TEAM], fetchTeam, restCacheConfig);
};

export const fetchuser = ({ queryKey }: any) => {
  if (!queryKey[1]) return;
  return RestService.get(`/users/` + queryKey[1]);
};

export const useUserData = (uuid: string) => {
  return useQueryData([QUERY_KEYS.USER, uuid], fetchuser, restCacheConfig);
};

export const fetchUsers = (e, text: string = '') => {
  return RestService.get(`/users?text=${text}&page=${e.pageParam || 1}`);
};

export const useUsersData = (text: string) => {
  return useInfiniteQueryData([QUERY_KEYS.USERS], e => fetchUsers(e, text), restCacheConfig);
};

export const fetchFollowers = ({ queryKey, pageParam }: any) => {
  return RestService.get(`/users/followers?userUuid=${queryKey[1]}&page=${pageParam}`);
};

export const useFollowersData = (uuid: any) => {
  return useInfiniteQueryData([QUERY_KEYS.FOLLOWERS, uuid], fetchFollowers, restCacheConfig);
};

export const fetchFollowings = ({ queryKey, pageParam }: any) => {
  return RestService.get(`/users/followings?userUuid=${queryKey[1]}&page=${pageParam}`);
};

export const useFollowingsData = (uuid: any) => {
  return useInfiniteQueryData([QUERY_KEYS.FOLLOWINGS, uuid], fetchFollowings, restCacheConfig);
};

export const fetchBlocked = ({ queryKey, pageParam }: any) => {
  return RestService.get(`/users/blocked?userUuid=${queryKey[1]}&page=${pageParam}`);
};

export const useBlockedData = (uuid: any) => {
  return useInfiniteQueryData([QUERY_KEYS.BLOCKED, uuid], fetchBlocked, restCacheConfig);
};

export const fetchInactive = ({ pageParam }) => {
  return RestService.get(`/users/inactive?page=${pageParam}`);
};

export const useInactiveData = () => {
  return useInfiniteQueryData([QUERY_KEYS.INACTIVE], fetchInactive, restCacheConfig);
};

export const isFollowing = (type: string) => type === 'Following';

export const createAction = (
  type: string,
  userUuid: string,
  businessUuid: string,
  frndUuid: string,
  postUuid: string = '',
  voteId: string = '',
  groupUuid: string = ''
) => {
  return Object.assign(
    {},
    {
      type,
      userUuid,
      businessUuid,
      frndUuid,
      uuid: uuidv4()
    },
    !!postUuid ? { postUuid } : {},
    !!voteId ? { voteId } : {},
    !!groupUuid ? { groupUuid } : {}
  );
};

const sendFollowUserData = async (type: string, user: any) => {
  const userUuid = useUserStore.getState().UUID;
  const businessUuid = useUserStore.getState().UBUUID;
  if (isFollowing(type)) {
    return RestService.delete(`actions/remove/${user.uuid}/${userUuid}/${ACTION_TYPES.USER_FOLLOW}`);
  } else {
    const data = createAction(ACTION_TYPES.USER_FOLLOW, userUuid, businessUuid, user.uuid);
    return RestService.post('/actions', data);
  }
};

const onMutateFollowUserData = async (queryClient: any, queryKey: any, userNew: any, type: string) => {
  const user = _.cloneDeep(userNew);
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    return {
      ...oldQueryData,
      data: {
        ...oldQueryData.data,
        isFollowing: !isFollowing(type) ? ++user.isFollowing : --user.isFollowing,
        followers: !isFollowing(type) ? ++user.followers : --user.followers
      }
    };
  });
};

const onMutateFollowUsersData = async (queryClient: any, queryKey: any, userNew: any, type: string) => {
  const user = _.cloneDeep(userNew);
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!oldData) return;
  queryClient.setQueryData(queryKey, (oldQueryData: any) => {
    user.isFollowing = !isFollowing(type) ? ++user.isFollowing : --user.isFollowing;
    if (!oldQueryData?.pages?.length) {
      return {
        ...oldQueryData,
        data: oldQueryData.data.map(item => (item.uuid === user.uuid ? user : item))
      };
    } else {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any) => {
          return {
            ...pageData,
            data: pageData.data.map(item => (item.uuid === user.uuid ? user : item))
          };
        })
      };
    }
  });
};

export const useFollowUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ type, user }: any) => sendFollowUserData(type, user),
    onMutate: async ({ type, user }: any) => {
      onMutateFollowUserData(queryClient, [QUERY_KEYS.USER, user.uuid], user, type);
      onMutateFollowUsersData(queryClient, [QUERY_KEYS.USERS], user, type);
      onMutateFollowUsersData(queryClient, [QUERY_KEYS.INACTIVE], user, type);
      onMutateFollowUsersData(queryClient, [QUERY_KEYS.SUGGESTIONS], user, type);
      const userUuid = useUserStore.getState().UUID;
      onMutateFollowUsersData(queryClient, [QUERY_KEYS.FOLLOWERS, userUuid], user, type);
      onMutateFollowUsersData(queryClient, [QUERY_KEYS.FOLLOWINGS, userUuid], user, type);
    }
  });
};

export const useUpdateUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ user, formData }: any) => RestService.update('/users/' + user.uuid, formData || user, formData && MULTIPART_HEADERS),
    onSuccess: async (newData, { user, isBlock = false }: any) => {
      await queryClient.cancelQueries({ queryKey: [QUERY_KEYS.USER, user.uuid] });
      const oldData = queryClient.getQueryData([QUERY_KEYS.USER, user.uuid]);
      if (!!oldData) {
        queryClient.setQueryData([QUERY_KEYS.USER, user.uuid], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: newData.data
          };
        });
      }
      const oldData2 = queryClient.getQueryData([QUERY_KEYS.USERS]);
      if (oldData2) {
        queryClient.setQueryData([QUERY_KEYS.USERS], (oldQueryData: any) => ({
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            if (isBlock) {
              if (user.status === USER_STATUS.INACTIVE) {
                return {
                  ...pageData,
                  data: pageData.data.filter(item => item.uuid !== user.uuid)
                };
              } else if (user.status === USER_STATUS.ACTIVE) {
                return {
                  ...pageData,
                  data: [user, ...pageData.data]
                };
              }
            } else {
              return {
                ...pageData,
                data: pageData.data.map(item => (item.uuid === user.uuid ? user : item))
              };
            }
            return pageData;
          })
        }));
      }
      const oldData3 = queryClient.getQueryData([QUERY_KEYS.INACTIVE]);
      if (oldData3) {
        queryClient.setQueryData([QUERY_KEYS.INACTIVE], (oldQueryData: any) => ({
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            if (isBlock) {
              if (user.status === USER_STATUS.ACTIVE) {
                return {
                  ...pageData,
                  data: pageData.data.filter(item => item.uuid !== user.uuid)
                };
              } else if (user.status === USER_STATUS.INACTIVE) {
                return {
                  ...pageData,
                  data: [user, ...pageData.data]
                };
              }
            } else {
              return {
                ...pageData,
                data: pageData.data.map(item => (item.uuid === user.uuid ? user : item))
              };
            }
            return pageData;
          })
        }));
      }
      const oldData4 = queryClient.getQueryData([QUERY_KEYS.USERS_ALL]);
      if (oldData4) {
        queryClient.setQueryData([QUERY_KEYS.USERS_ALL], (oldQueryData: any) => {
          return {
            ...oldQueryData,
            data: oldQueryData.data.map(item =>
              item.uuid === user.uuid
                ? Object.assign(
                    {},
                    item,
                    { uuid: user.uuid, name: user.name, image: user.image || '' },
                    user.reporterUuid && { reporterUuid: user.reporterUuid }
                  )
                : item
            )
          };
        });
      }
    }
  });
};

const onSuccessAddUsersData = async (queryClient: any, queryKey: any, newData: any) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      return {
        ...oldQueryData,
        pages: oldQueryData.pages.map((pageData: any, index: number) => {
          return {
            ...pageData,
            data: index === 0 ? [newData.data, ...pageData.data] : pageData.data
          };
        })
      };
    });
  }
  const oldData1 = queryClient.getQueryData([QUERY_KEYS.USERS_ALL]);
  if (!!oldData1) {
    queryClient.setQueryData([QUERY_KEYS.USERS_ALL], (oldQueryData: any) => {
      const newUser = Object.assign(
        { uuid: newData.data.uuid, name: newData.data.name, image: newData.data.image || '' },
        newData.data.reporterUuid && { reporterUuid: newData.data.reporterUuid }
      );
      return {
        ...oldQueryData,
        data: [newUser, ...oldQueryData.data]
      };
    });
  }
};

export const useAddUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (user: any) => {
      const businessUuid = useUserStore.getState().UBUUID;
      const data = _.assign({}, user, {
        businessUuid,
        uuid: uuidv4(),
        type: USER_TYPE.EMPLOYEE
      });
      return RestService.post('/users', data);
    },
    onSuccess: (newData: any) => {
      onSuccessAddUsersData(queryClient, [QUERY_KEYS.USERS], newData);
    }
  });
};

export const useImportUsersMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (users: any) => {
      const businessUuid = useUserStore.getState().UBUUID;
      const data = users.map(user =>
        Object.assign(user, {
          businessUuid,
          uuid: uuidv4(),
          reporterUuid: businessUuid,
          type: USER_TYPE.EMPLOYEE
        })
      );
      return RestService.post('/users/import', data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS_ALL] });
    }
  });
};

export const useDeleteUserMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (uuid: string) => RestService.delete('/users/' + uuid),
    onSuccess: async (newData, uuid) => {
      const userUuid = useUserStore.getState().UUID;
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.USERS], uuid);
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.SUGGESTIONS], uuid);
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.FOLLOWERS, userUuid], uuid);
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.FOLLOWINGS, userUuid], uuid);
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.INACTIVE], uuid);
      await onSuccessDeleteUsersData(queryClient, [QUERY_KEYS.BLOCKED], uuid);
    }
  });
};

const onSuccessDeleteUsersData = async (queryClient: any, queryKey: any, uuid: string) => {
  await queryClient.cancelQueries({ queryKey });
  const oldData = queryClient.getQueryData(queryKey);
  if (!!oldData) {
    queryClient.setQueryData(queryKey, (oldQueryData: any) => {
      if (oldQueryData.pages) {
        return {
          ...oldQueryData,
          pages: oldQueryData.pages.map((pageData: any) => {
            return {
              ...pageData,
              data: pageData.data.filter(item => item.uuid !== uuid)
            };
          })
        };
      }
      return {
        ...oldQueryData,
        data: oldQueryData.data.filter(item => item.uuid !== uuid)
      };
    });
  }
};

export const isModerator = (role?: any | any[]) => {
  if (_.isArray(role)) {
    return role.some(r => [USER_ROLES.ADMIN, USER_ROLES.EDITOR].includes(r));
  }
  const userRoles = useUserStore.getState().UROLE;
  if (_.isArray(userRoles)) {
    return userRoles.some(r => [USER_ROLES.ADMIN, USER_ROLES.EDITOR].includes(r));
  }
  const userRole = role || userRoles;
  return [USER_ROLES.ADMIN, USER_ROLES.EDITOR].includes(userRole);
};

export const isAdmin = (role?: any | any[]) => {
  if (_.isArray(role)) {
    return role.some(r => [USER_ROLES.ADMIN].includes(r));
  }
  const userRoles = useUserStore.getState().UROLE;
  if (_.isArray(userRoles)) {
    return userRoles.some(r => [USER_ROLES.ADMIN].includes(r));
  }
  const userRole = role || userRoles;
  return [USER_ROLES.ADMIN].includes(userRole);
};

export const isExternal = (role?: any) => {
  const userRole = role || useUserStore.getState().UROLE;
  if (_.isArray(userRole)) {
    return userRole.includes(USER_ROLES.EXTERNAL);
  }
  return userRole === USER_ROLES.EXTERNAL;
};

export const hasSpecificAccess = (type: USER_ROLES) => {
  const userRoles = useUserStore.getState().UROLE;
  if (_.isArray(userRoles)) {
    return userRoles.some(r => [USER_ROLES.ADMIN, USER_ROLES.EDITOR, type].includes(r));
  }
  return [USER_ROLES.ADMIN, USER_ROLES.EDITOR, type].includes(userRoles);
};

export const isBot = (userUuid: string) => {
  return userUuid === CONFIG.BOT_UUID;
};
